import { RtfFormat } from "@/features/document-types/document-builder/components/common/ExportSettingsModal";
import { TimezoneOptions } from "./timezone";
import { IntegrationProvider } from "@/features/settings/components/integrations/Integrations";
import { MonitoringGridPayload } from "./monitoring";

//Open AI format for chat completions
export type OpenAIChatCompletions = {
    role: string;
    content: string | React.JSX.Element;
};

export type AppVersion = {
    id: number;
    version: number;
};

export enum DoctorDocumentTypeContext {
    Default = "default",
    History = "history",
}

export type DoctorDocumentType = {
    document_type_id: string;
    doctor_id?: string;
    context?: DoctorDocumentTypeContext;
};

export enum ValidateType {
    Doctor = "doctor",
    Communication = "communication",
}

export enum CommunicationType {
    Email = "email",
    Sms = "sms",
}

//DATABASE TYPES
export type Doctor = {
    id?: string;
    account_id?: string;
    created_at?: string | null;
    doctor_name?: string | null;
    email?: string | null;
    email_confirmed_at?: string | null;
    confirmation_sent_at?: string | null;
    confirmation_token?: string | null;
    doctor_document_types?: DoctorDocumentType[];
    sort_index?: number;
    pms_employee_id?: string | null;
    pms_export_id?: string;
    pms_first_name?: string | null;
    pms_last_name?: string | null;
    is_archived?: boolean;
    sync_excluded?: boolean;
    doctor_name_source?: "cornerstone" | "manual" | null;
    /** Write-only: set true to hand a manually-set name back to PIMS sync control. */
    reset_name_source?: boolean;
};

export type Resource = {
    id?: string;
    account_id?: string;
    resource_name: string;
    pms_resource_id?: string;
    sort_index: number;
    is_archived?: boolean;
    sync_excluded?: boolean;
    resource_name_source?: "cornerstone" | "manual" | null;
    /** Write-only: set true to hand a manually-set name back to PIMS sync control. */
    reset_name_source?: boolean;
    created_at?: string;
};

export type DocumentType = {
    id: string;
    document_type_name: string;
    account_id?: string;
    created_at?: string | null;
    output_parser?: JSON | null;
    prompt?: string;
    prompt_components?: JSON | null;
    prompt_override?: boolean;
    training_data?: JSON | null;
    auto_generate?: boolean;
    is_default?: boolean;
    is_archived?: boolean;
    description?: string;
    last_updated_at?: string;
    is_active?: boolean;
    creator_prompt_components?: SectionFormatting[] | null;
    custom_document_type?: string | null;
    is_json?: boolean;
    document_exports?: DocumentExport | null;
    document_foundation_id?: string | null;
    dependency_data?: DocumentDependencyData | null;
    output_language?: string;
};

export type PartialDocumentType = {
    id: string;
    document_type_name: string;
    is_archived: boolean;
    is_json: boolean | null;
};

export type ShepherdTemplateFieldSource = { type: "section"; stableFieldId: string } | { type: "entire_document" };
export type ShepherdTemplateFieldMapEntry = { templateField: string; source: ShepherdTemplateFieldSource };

export type EzyvetClinicalTarget = "VisitHistory" | "VisitExam" | "ConsultAssessment" | "ConsultPlan" | "Revisit";
export type EzyvetSectionMapEntry = { target: EzyvetClinicalTarget; stableFieldId: string };

/**
 * Represents a record in the document_exports table.
 */
export type DocumentExport = {
    /**
     * The id of the export
     */
    id: string;

    /**
     * when the export was created
     */
    created_at: string | null;

    /**
     * what account the export is associated with
     */
    account_id: string;

    /**
     * what account integration the export is associated with
     */
    account_integrations_id: string | null;

    /**
     * true if the export is active
     */
    is_active: boolean;

    /**
     * the url of the export, relative to the base storage url
     */
    url: string;

    /**
     * the type of the export (pdf, docx, etc.)
     */
    content_type: string | null;

    /**
     * if the export is a custom document type
     */
    is_custom: boolean | null;

    /**
     * the font of the export
     */
    font_family: string | null;

    /**
     * the template key of the export
     */
    template_key: RtfFormat | null;

    /**
     * the note type of the export (Note or SOAP)
     */
    note_type: "Note" | "SOAP" | null;

    /**
     * per-document-type override for the Cornerstone record color ("#RRGGBB"), or null to
     * inherit the account-level default (account_integrations.cornerstone_default_alert_color)
     */
    cornerstone_alert_color: string | null;

    /**
     * explicit "never color this document type" — independent of and takes priority over
     * cornerstone_alert_color and the account default
     */
    cornerstone_alert_disabled: boolean | null;

    /**
     * Shepherd physical-exam template this export fills (null when not mapped to a template)
     */
    shepherd_template_id?: string | null;
    shepherd_template_name?: string | null;

    /**
     * mapping of Shepherd template fields to document content
     */
    shepherd_template_field_map?: ShepherdTemplateFieldMapEntry[] | null;

    /**
     * ezyVet record class per document section; null = match sections by name (default)
     */
    ezyvet_section_map?: EzyvetSectionMapEntry[] | null;
};

// Represents the structural dependency data of a document
export type DocumentDependencyData = {
    needsUpdate: boolean;
    dependencies: DocumentDependencies;
};

// Represents the structural dependencies within a document
export type DocumentDependencies = Record<string, string[]>;

export enum VisitPage {
    List = "list",
    Calendar = "calendar",
}

export enum PatientStage {
    Ongoing = "ONGOING",
    Review = "REVIEW",
    Finalized = "FINALIZED",
}

export type Transcript = {
    id?: string;
    created_at?: string;
    doctor_id?: string | null;
    patient_record_id?: string;
    transcript_sections?: TranscriptSection[];
};

export enum DocumentStatus {
    Completed = "completed",
    Processing = "processing",
    Failed = "failed",
    Queued = "queued",
    Cancelled = "cancelled",

    // local status only
    Regenerating = "regenerating",
}

export type ChatText = string | DisplaySection[]; // Legacy document can be a string
export type ChatJson = {
    header?: string;
    chat_text?: ChatText | null;
    document_regenerated?: boolean;
};

export type ChatCompletions = {
    account_id?: string | null;
    chat_json: ChatJson | null;
    chat_json_original: ChatJson | null;
    created_at?: string | null;
    updated_at?: string | null;
    document_type_id?: string | null;
    id?: string | null;
    patient_record_id?: string | null;
    role?: string | null;
    transcription_id?: string | null;
    type?: string | null;
    feedback?: string | null;
    exported_at?: string | null;
    exported_to?: string | null;
    logs?: any;
    count?: number | null;
    feedback_description?: string | null;
    document_types?: DocumentType | null;
    document_snapshot?: SectionFormatting[] | null;
    status?: DocumentStatus;
    header?: string | null;
    is_edited?: boolean | null;
    is_archived?: boolean | null;
    transcript_section_ids?: string[] | null;
    document_asset_ids?: string[] | null;
    monitoring_date_key?: string | null;
    sent_at?: string | null;
};

export type ChatCompletionAsset = {
    chat_completion_id: string;
    transcript_section_id: string | null;
    document_asset_id: string | null;
};

export type DisplayDocument = DisplaySection[];

export type ScoutDocumentStatus = "processing" | "pending";
export type PendingDisplayDocumentChanges = {
    content: DisplayDocument | string | null | undefined;
    changes: DisplayDocument | string | null | undefined;
    accepted: boolean;
};
export type PendingDisplayDocument = PendingDisplayDocumentChanges & {
    tabId: string;
    documentIndex: number;
};

/**
 * Represents a section of the document that is ready to be displayed by any UI.
 */
export interface DisplaySection {
    /**
     * The ID of the section. Included for reference.
     */
    id: string;

    /**
     * The name of the section.
     */
    name: string;

    /**
     * The value of the section.
     */
    value: string | string[];

    /**
     * The formatting of the section. Included so that the UI can display arrays correctly at runtime.
     */
    formatting: FormattingOption;

    /**
     * The data type of the section.
     */
    field_type: DocumentFieldType;

    /**
     * The ordered fields of the section.
     */
    fields: DisplayField[];

    /**
     * Boolean indicating if the section has errors.
     */
    hasErrors?: boolean;

    /**
     * Boolean indicating if the section is hidden.
     */
    isHidden?: boolean;

    /**
     * Whether the field is normal. Included so that the UI can display bolded fields correctly at runtime.
     */
    isNormal: boolean;

    /**
     * Whether the section is bold. Derived from isNormal during document generation but can be changed by the user.
     */
    isBold: boolean;

    /**
     * Boolean indicating if the field is a title.
     */
    boldTitle?: boolean;

    /**
     * Boolean indicating if the field is specifically for an export document
     */
    isExport?: boolean;

    grid?: MonitoringGridPayload;
}

/**
 * Represents a field of the document that is ready to be displayed by any UI.
 */
export interface DisplayField {
    /**
     * The ID of the field. Included for reference.
     */
    id: string;

    /**
     * The name of the field.
     */
    name: string;

    /**
     * The value of the field.
     */
    value: string | string[];

    /**
     * The formatting of the field. Included so that the UI can display arrays correctly at runtime.
     */
    formatting: FormattingOption;

    /**
     * The data type of the field.
     */
    field_type: DocumentFieldType;

    /**
     * Empty fields array means the field is a leaf node.
     */
    fields: [];

    /**
     * Whether the field is normal. Included so that the UI can display bolded fields correctly at runtime.
     */
    isNormal: boolean;

    /**
     * Whether the section is bold. Derived from isNormal during document generation but can be changed by the user.
     */
    isBold: boolean;

    /**
     * Boolean indicating if the field has been edited by the user.
     */
    isEdited?: boolean;
    field_regenerated?: boolean;

    /**
     * Data type indicating the source of the response. Currently (USER_DEFINED, GPT, EXPORT)
     */
    response_source: DocumentResponseSources;

    /**
     * Boolean indicating if the field is hidden.
     */
    isHidden?: boolean;

    /**
     * Boolean indicating if the field is a title.
     */
    boldTitle?: boolean;

    /**
     * Boolean indicating if the field is specifically for an export document
     */
    isExport?: boolean;
}

export interface FieldFormatting {
    id: string;
    field_name: string;
    normal_values: string;
    formatting?: FormattingOption;
    field_type?: DocumentFieldType;
    displayTitle?: boolean;
    displayOutput?: boolean;
    display_transcript_value?: DisplayIfDiscussedOptions;
    display_if_not_discussed?: DisplayIfNotDiscussedOptions;
    display_if_not_discussed_specified_value?: string;
    boldTitle?: boolean;
    customPrompt?: string;
}

export interface SectionFormatting {
    id: string;
    section_name: string;
    formatting: FormattingOption;
    field_type?: DocumentFieldType;
    normal_values: string;
    displayTitle?: boolean;
    displayOutput?: boolean;
    display_transcript_value?: DisplayIfDiscussedOptions;
    display_if_not_discussed?: DisplayIfNotDiscussedOptions;
    display_if_not_discussed_specified_value?: string;
    fields: FieldFormatting[];
    boldTitle?: boolean;
    customPrompt?: string;
}

export type DocumentTab = {
    //document_type_id + header
    tabId: string;
    //document header
    header: string | undefined;
    //Group of records
    documents: ChatCompletions[];
    // Status of any Scout operations by document index
    scoutDocumentStatus: { [key: string]: ScoutDocumentStatus };
    // Document changes waiting for approval for the tab by document index
    pendingDocuments: { [key: number]: PendingDisplayDocument };
    // is tab error
    error: string | null;
};

export interface HDTab {
    // value to display
    header: string;
    // unique value for the tab
    id: string;
    // is active tab
    isActive: boolean;
    // show count of something
    count?: number;
    // show icon for tab
    icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
    // show error dot
    error?: boolean;
    // disable individual tab
    disabled?: boolean;
    // rendered after the label, e.g. a tag
    suffix?: React.ReactNode;
    /** Vertical rails only: renders a small uppercase heading above this tab, starting a group.
     *  Horizontal tab bars ignore it. */
    group?: string;
}

export type AccountMetaData = {
    markdown_copy_enabled?: boolean;
    /** EN-1075: clinic-wide opt-out of the confetti burst when a visit moves to Finalized. */
    finalize_confetti_disabled?: boolean;
    /** EN-1414: clinic-wide opt-out of the floating Support, Scout and idle Quick Start buttons. */
    floating_buttons_hidden?: boolean;
    default_view: "list" | "calendar";
    role?: string;
};

export enum AccountStatus {
    Active = "Active",
    Trialing = "Trialing",
    Lost = "Lost",
    TrialExpired = "Trial Expired",
}

export type Account = {
    id?: string;
    clinic_location?: string | null;
    clinic_name?: string | null;
    clinic_time_zone?: TimezoneOptions | null;
    created_at?: string | null;
    is_self_serve?: boolean;
    feature_flags?: string[] | null;
    meta_data?: AccountMetaData | null;
    api_key?: string | null;
    current_pims?: PIMSType | null;
    hidden_documents?: string[] | null;
    auth_email?: string | null;
    trial_end_date?: string | null;
    has_completed_profile?: boolean;
    communications_enabled?: CommunicationType[];
    status?: AccountStatus;
    clinic_phone_number?: string | null;
    clinic_banner_image_url?: string | null;
    /** When false or unset, entire pins feature (lock screen / device access) is disabled. */
    pins_enabled?: boolean;
    /** Enables Team & Access management UI. */
    account_users_enabled?: boolean;
    /** When true, the clinic has HappyInsights access (hides the dashboard upsell CTA). */
    insights_enabled?: boolean;
    /**
     * HappyBridge beta access. Since EN-1063 this gates the raw PIMS picker and installer download
     * on Settings → Integrations, and is one of the two ways to reach the install wizard route (the
     * other being `bridge_upgrade_offer`). It no longer gates the upgrade CTA — that moved to
     * `bridge_upgrade_offer` below, which is the whole point of the split.
     *
     * Lives on `account_settings` only (migration 20260708120000); there is no such column on
     * `accounts`. Flattened onto this type by both account-fetch paths in supabaseClient, so read
     * it from here rather than reaching for a settings object.
     */
    happy_bridge_beta?: boolean;
    /**
     * EN-1063. "Should this clinic be pitched the HappyBridge upgrade", as distinct from
     * `happy_bridge_beta`, which means "is in the bridge beta". The two used to be the same
     * column and therefore the same accounts, and since nearly every account in the beta had
     * already finished setup, the offer rendered for zero real Cornerstone clinics.
     *
     * Lives on `account_settings` only (migration 20260922135955); there is no such column on
     * `accounts`. Flattened onto this type by both account-fetch paths in supabaseClient, same
     * as `happy_bridge_beta`. Flipped per account in Spork — rollout is by cohort because the
     * offer's booking link is bounded by calendar capacity, not code.
     */
    bridge_upgrade_offer?: boolean;
    /**
     * Gradual, admin-controlled rollout gate for the Monitoring document type (set via spork).
     * Lives on `account_settings` only (migration 20260824170000); there is no such column on
     * `accounts`. Flattened onto this type by both account-fetch paths in supabaseClient, same
     * as `happy_bridge_beta`.
     */
    monitoring_enabled?: boolean;
    /**
     * Spork-admin per-account toggle for happy-bridge's Cornerstone change-detection poller.
     * Lives on `account_settings` only; there is no such column on `accounts`. Flattened onto
     * this type by both account-fetch paths in supabaseClient, same as `happy_bridge_beta`. Used
     * client-side to gate the periodic patient_record refresh in DataProvider.tsx — polling for
     * an account that doesn't have change detection enabled would never find anything new.
     */
    happy_bridge_change_detection_enabled?: boolean;
    /** Whether the session user is owner of this clinic account (from resolved API when team accounts are enabled, else Supabase). */
    viewer_is_account_owner?: boolean;
    phone_calls_enabled?: boolean;
    preferred_provider?: LLMProviders;
    account_group_id?: string | null;
    payment_processor_id?: string | null;
    /** Controls whether the calendar defaults to showing doctors or resources. */
    calendar_mode?: "doctors" | "resources" | null;
};

/** Device access mode for shared devices (lock screen / PIN). */
export type DeviceAccessMode = "off" | "pin" | "switch";

export interface DeviceAccessSettings {
    device_access_mode: DeviceAccessMode;
    auto_lock_timeout_minutes: number;
    max_pin_attempts: number;
}

export type BillingType = "standard" | "per_dvm" | "group" | "free" | "trial";
export type AccountTier = "scribe" | "assistant" | "advisor" | "founders" | "trial";

export interface BillingPlan {
    tier: AccountTier | null;
    billing_type: BillingType;
    plan_name: string;
    billing_interval: "month" | "year" | null;
    usage: {
        current: number;
        limit: number | null;
        percent_used: number | null;
        is_over_limit: boolean;
        overage_count: number;
        metric: "happy_visits" | "doctors";
    } | null;
    next_invoice: {
        date: string | null;
        amount: number | null;
        currency: string | null;
    } | null;
    group_name: string | null;
    trial_days_remaining: number | null;
    trial_end_date: string | null;
    trial_total_days: number | null;
    stripe_subscription_id: string | null;
    unpaid_invoice: {
        amount_due: number;
        currency: string;
        due_date: string | null;
        hosted_invoice_url: string | null;
    } | null;
    /** Stripe product IDs for active subscription items (used to detect purchased addons) */
    active_product_ids?: string[];
    /** Whether the account has PIMS integrations enabled (purchased or provisioned) */
    integrations_enabled: boolean;
    all_time_happy_visits: number;
}

// Stripe invoice statuses — note: past_due is a subscription status, NOT an invoice status.
export type InvoiceStatus = "draft" | "open" | "paid" | "void" | "uncollectible";

export interface BillingInvoice {
    id: string;
    amount_due: number;
    amount_paid: number;
    currency: string;
    status: InvoiceStatus;
    created: number;
    due_date: number | null;
    hosted_invoice_url: string | null;
    invoice_pdf: string | null;
    description: string | null;
    period_start: number | null;
    period_end: number | null;
}

export type Communications = {
    id?: string;
    account_id?: string;
    communication_type?: CommunicationType;
    value?: string | null;
    is_active?: boolean;
    is_primary?: boolean;
    confirmed_at?: string | null;
    confirmation_sent_at?: string | null;
    confirmation_token?: string | null;
    signature?: string | null;
    signature_json?: any | null;
    cc_active?: boolean;
    created_at?: string | null;
    updated_at?: string | null;
};

export interface Address {
    id: string;
    account_id: string;
    street_address: string;
    street_address_2?: string;
    city: string;
    state_region: string;
    postal_code: string;
    country: string;
    latitude?: number;
    longitude?: number;
    created_at?: string;
    updated_at?: string;
}

export type UnNestedPatientRecordData = {
    account_id?: string;
    appointment_duration?: number;
    created_at?: string | null;
    doctor_id?: string | null;
    id?: string;
    patient_id?: string | null;
    patient_name?: string | null;
    status?: string | null;
    scheduled_at?: string | null;
    stage?: PatientStage;
};

export enum TranscriptSectionStatus {
    NotStarted = "not_started",
    Uploaded = "uploaded",
    InProgress = "in_progress",
    Cancelled = "cancelled",
    Completed = "completed",
    FailedIncompleteUpload = "failed_incomplete_upload",
    FailedBlankTranscript = "failed_blank_transcript",
    FailedErrorProcessing = "failed_error_processing",
    FailedIrrelevantInformation = "failed_irrelevant_information",
    FailedPartialTranscription = "failed_partial_transcription",
}

export const TranscriptSectionStatusFailedDisplay = {
    [TranscriptSectionStatus.FailedIncompleteUpload]: "Incomplete upload.",
    [TranscriptSectionStatus.FailedBlankTranscript]: "Transcript is blank. Check the clip for valid audio and retry.",
    [TranscriptSectionStatus.FailedErrorProcessing]: "Error in transcript processing. Retry.",
    [TranscriptSectionStatus.FailedIrrelevantInformation]: "Transcript does not contain enough relevant content.",
};

export type TranscriptSection = {
    id: string;
    transcription_id?: string;
    patient_record_id?: string;
    data?: string;
    unprocessed_filename?: string;
    processed_filename?: string;
    was_removed?: boolean;
    created_at?: string;
    account_id?: string;
    retried_automatically?: boolean;
    uploadProgress?: number | null;
    count?: number | null;
    audio_duration?: number | null;
    status?: TranscriptSectionStatus | null;
    origin?: string | null;
    display_name?: string | null;
    is_archived?: boolean;
};

export enum DocumentAssetStatus {
    Uploading = "uploading",
    Uploaded = "uploaded",
    Failed = "failed",
}

export type DocumentAsset = {
    id: string;
    created_at: string;
    file_name: string;
    file_size: number;
    file_type: string;
    /** Where the file actually is in the `assets` bucket. Not derivable from file_name: the stored
     *  object name carries a random suffix, and file_name changes on rename (EN-1304). */
    storage_path?: string | null;
    status?: DocumentAssetStatus;
    /** Client-only: why the upload failed, when the server gave a reason the vet can act on. */
    uploadErrorMessage?: string;
};

export type RenderableAsset = {
    id: string;
    name: string;
    type: "clip" | "file";
    created_at: string;
};

export enum AssetType {
    Clip = "clip",
    Document = "document",
}

/**
 * This is the union of TranscriptSection and DocumentAsset
 * (clip or pdf in the library)
 */
export type Asset = {
    // shared
    id: string;
    name: string;
    type: AssetType;
    created_at: string;

    // section specific
    data?: string;
    audio_duration?: number | null;
    status?: TranscriptSectionStatus | null;

    // document specific
    size?: number;
    fileType?: string;
};

export type PartialDoctor = Pick<Doctor, "id" | "doctor_name" | "pms_employee_id" | "pms_export_id">;
export type PartialResource = Pick<Resource, "id" | "resource_name">;

export type PatientRecord = {
    id: string;
    /** A stub for a past PIMS visit HappyDoc did not record. */
    is_history?: boolean | null;
    appointment_duration?: number;
    doctors?: PartialDoctor | null;
    patient_id?: string | null;
    patient_id_new?: string | null;
    patient_name?: string | null;
    status?: string | null;
    stage?: PatientStage;
    species?: string | null;
    gender?: string | null;
    transcriptions?: Transcript | null;
    scheduled_at?: string | null;
    chat_completions?: ChatCompletions[] | null;
    doctor_id?: string | null;
    resource_id?: string | null;
    excluded_resource_id?: string | null;
    contacts?: Contact | null;
    patients?: Patient | null;
    is_archived?: boolean | null;
    account_integrations?: AccountIntegration | null;
    type?: string | null;
    /** PIMS-native appointment-type color, e.g. Cornerstone's appt_types.type_color decoded to "#RRGGBB". Null when the PIMS has none set, or for non-Cornerstone integrations. */
    pims_type_color?: string | null;

    document_assets?: DocumentAsset[] | null;

    linked_by_integration?: boolean | null;
    created_at?: string | null;
    updated_at?: string | null;
};

export type AddPatientRequest = {
    patientRecordId?: string;
    userId?: string;
    doctorId?: string;
    resourceId?: string;
    patientName?: string;
    patientId?: string;
    scheduledAt?: string;
    appointmentDuration?: number;
    contactId?: string;
    stage?: PatientStage;
};

export type SignUpRequest = {
    email: string;
    password: string;
    emailRedirectTo: string;
    doctorName?: string;
    promotion?: string;
    recaptchaToken?: string;
    heardAboutUs?: string;
};

export interface SearchParams {
    collections: TypeSenseCollections[];
    query: string;
    filterBy?: string;
    sortBy?: string;
    page?: number;
    perPage?: number;
}

/**
 * EN-1255: one number the PIMS holds for a client (`contacts.phones`). Mirrors API-Server
 * `app/domain/contactPhones.ts` ContactPhone. `number` is a clean dial string (digits only);
 * `raw` is exactly what the PIMS sent and is display-only.
 */
export type ContactPhone = {
    number: string;
    label: string | null;
    is_primary: boolean;
    raw: string;
    /**
     * Set when the PIMS raw text contained more than one distinct valid dial number. `number` is the
     * first valid one; staff should verify it against `raw`. Omitted when unambiguous.
     */
    ambiguous?: true;
};

export type Contact = {
    id: string;
    created_at?: string;
    first_name?: string;
    last_name?: string;
    primary_phone?: string;
    phones?: ContactPhone[] | null; // NULL = never populated; read as []
    primary_email?: string;
    account_id?: string;
    account_integrations_id?: string;
    external_id?: string;
    secondary_id?: string;
};

export type Patient = {
    id: string;
    name?: string;
    sex?: string;
    species?: string;
    breed?: string;
    dob?: string;
    secondary_id?: string;
    account_id?: string;
    account_integrations_id?: string;
    external_id?: string;
    integration_data?: string;
    created_at?: string;
    is_archived?: boolean;
    updated_at?: string;
};

export enum TypeSenseCollections {
    Patients = "patients",
    PatientRecord = "patient_record",
    Contacts = "contacts",
}

export const enum RecordingState {
    inactive = "inactive",
    recording = "recording",
    paused = "paused",
    error = "error",
    no_permission = "no-permission",
    no_device = "no-device",
    loading = "loading",
}

export const enum FileUploadingState {
    inactive = "inactive",
    uploading = "uploading",
    local_storage_error = "local_storage_error",
}

export const enum MedicalRecordState {
    empty = "Empty", // No audio, no documents
    processing = "Processing", // at least one audio or document loading
    generated = "Generated", // at least one document generated
    completed = "Completed", // user acted on a document
    error = "Error",
    transcribed = "Transcribed", // at least one audio transcribed, but no documents
    FileSizeError = "Unsupported File Size",
    Uploading = "Uploading",
    //These are client side only
    recording = "Recording",
    paused = "Paused",
}

export type AccountIntegration = {
    id?: string | null;
    practice_id?: string | null;
    connected_third_party?: string | null;
    last_request?: string | null;
    last_request_status?: string | null;
    api_url?: string | null;
    export_enabled?: boolean | null;
    integrations?: Integration | null;
    is_active?: boolean | null;
    integration_name?: string | null;
    /** Account-level default Cornerstone record color ("#RRGGBB"), or null/undefined for no color. */
    cornerstone_default_alert_color?: string | null;
};

export type Integration = {
    integration_name?: IntegrationProvider | null;
};

// stripe

export interface Appointment {
    patient_name: string;
    id: string;
    patient_id: string;
    scheduled_at: string;
    appointment_duration: number;
    columnIndex: number;
    doctors: Doctor;
    /** Pulse appointment id; upsert key for Pulse sync (EN-484). */
    pulse_id?: string | null;
    /** When set to a non-empty ISO timestamp, the appointment is hidden from list UIs. */
    soft_deleted_at?: string | null;
    soft_deleted_reason?: string | null;
}

export interface ReleaseNotesItem {
    issue_type: ReleaseNotesIssueType;
    description: string;
}

export interface ReleaseNotes {
    id: string;
    created_at: string;
    release_date: string;
    is_visible: boolean;
    items: ReleaseNotesItem[];
}

export enum FeatureFlags {
    HIDE_TRANSCRIPTIONS = "HIDE_TRANSCRIPTIONS",
    ENABLE_ADVANCED_DOCUMENT_CREATION = "ENABLE_ADVANCED_DOCUMENT_CREATION",
    ENABLE_CUSTOM_DOCUMENT_CREATION = "ENABLE_CUSTOM_DOCUMENT_CREATION",
    JSON_DOCUMENT_CREATION = "JSON_DOCUMENT_CREATION",
    ENABLE_CUSTOM_PROMPT = "ENABLE_CUSTOM_PROMPT",
    ENABLE_TRANSCRIPTS_BUTTON = "ENABLE_TRANSCRIPTS_BUTTON",
    ENABLE_META_FIELDS = "ENABLE_META_FIELDS",
    ENABLE_CREATE_EXPORT = "ENABLE_CREATE_EXPORT",
    ENABLE_IMPORT_APPOINTMENTS = "ENABLE_IMPORT_APPOINTMENTS",
    CHUNK_UPLOAD = "ENABLE_CHUNK_UPLOAD",
    ENABLE_FILE_UPLOAD = "ENABLE_FILE_UPLOAD",
    ENABLE_SELECT_LLM = "ENABLE_SELECT_LLM",
}

export enum LLMProviders {
    OpenAI = "OpenAI",
    Cerebras = "Cerebras",
    Gemini = "Gemini",
    Azure = "Azure",
    Claude = "Claude",
}

export enum SectionType {
    SECTION = "section",
    FIELD = "field",
    JSON = "JSON",
    META = "meta-beginning",
}

export enum ReservedSectionNames {
    DocumentProgress = "documentProgress",
}

export type RecordComponent = CreatorPromptField & {
    fields?: CreatorPromptField[];
};

// Categories
export type CreatorPromptCategory = {
    id?: string;
    display_name?: string;
};

export type CreatorPromptFieldCategory = {
    id: string;
    field_id: string;
    category_id: string;
};

// Custom field category
export const CUSTOM_FIELD_CATEGORY: CreatorPromptCategory = {
    id: "589c9735-2508-47eb-8984-b2a867909d0d",
    display_name: "Custom Fields",
};

//Field
export type CreatorPromptField = {
    id: string;
    stableId?: string;
    allVersionIds?: string[];
    created_at?: string | null;
    field_name?: string;
    other_names?: string[];
    section_type?: SectionType;
    instructions?: string;
    normal_values?: string;
    description?: string;
    field_description?: string;
    example?: string;
    formatting?: FormattingOption;
    default_format?: FormattingOption;
    formatting_options?: FormattingOption[];
    field_type?: DocumentFieldType;
    display_transcript_value?: DisplayIfDiscussedOptions;
    display_transcript_options?: DisplayIfDiscussedOptions[];
    display_if_not_discussed?: DisplayIfNotDiscussedOptions;
    display_if_not_discussed_specified_value?: string;
    response_source?: DocumentResponseSources;
    hidden?: boolean;
    displayTitle?: boolean;
    displayOutput?: boolean;
    categories?: CreatorPromptCategory[];
    account_id?: string;
    display_output_default?: boolean;
    display_title_default?: boolean;
    example_prompts?: ExamplePrompt[];
    customPrompt?: string;
    customPromptOptions?: string[];
    is_archived?: boolean;
    boldTitle?: boolean;
    model_level: ModelLevel;
};

export type ExamplePrompt = {
    id: string;
    created_at: string;
    name: string | null;
    example: string | string[];
    field_id: string | null;
    species: string | null;
};

export type CustomFieldMeta = {
    account_id?: string;
    field_id?: string;
    custom_prompt?: string;
    document_type_id?: string;
};

export interface Stage {
    name: string;
    icon: React.FunctionComponent<
        React.SVGProps<SVGSVGElement> & {
            title?: string;
        }
    >;
    value: PatientStage;
}

// Type guard to check if the section is CreatorPromptField
export function isCreatorPromptField(section: any): section is CreatorPromptField {
    return (section as CreatorPromptField).field_name !== undefined;
}

export enum FormattingOption {
    Paragraph = "paragraph",
    Bulleted = "unordered-list",
    Dashed = "dashed-list",
    Numbered = "ordered-list",
    FlatList = "flat-list",
    None = "none",
    Grid = "grid",
}

export enum Species {
    canine = "canine",
    feline = "feline",
    equine = "equine",
    other = "other",
    bird = "bird",
    reptile = "reptile",
    rodent = "rodent",
    fish = "fish",
    cattle = "cattle",
    pig = "pig",
}

/**
 * User-selectable formatting options and their display labels.
 *
 * Intentionally excludes FormattingOption.Grid: the grid row/column schema is
 * fixed in application code and is never hand-authored in the document-type
 * builder, so "Grid" must not appear in any Format dropdown.
 */
export enum FormattingOptionToDisplay {
    "paragraph" = "Paragraph",
    "unordered-list" = "Bulleted",
    "dashed-list" = "Dashed",
    "ordered-list" = "Numbered",
    "flat-list" = "Flat List",
    "none" = "None",
}

/**
 * Display label for a formatting option. Returns an empty string for options
 * that are not user-selectable (e.g. FormattingOption.Grid).
 */
export function getFormattingOptionDisplay(option: FormattingOption): string {
    return (FormattingOptionToDisplay as Record<string, string>)[option] ?? "";
}

export enum DocumentFieldType {
    Text = "text",
    Checkbox = "checkbox",
    Table = "table",
    Picklist = "picklist",
    Number = "number",
}

export enum DocumentResponseSources {
    USER_DEFINED = "USER_DEFINED",
    GPT = "GPT",
    EXPORT = "EXPORT",
}

// represents public.display_transcript_options -> for 'If discussed' field
export enum DisplayIfDiscussedOptions {
    always = "always",
    abnormal = "abnormal",
}

// display options for DisplayIfDiscussedOptions
export enum DisplayIfDiscussedOptionsToDisplay {
    "always" = "Display discussed value always",
    "abnormal" = "Display discussed value if abnormal, otherwise use normal value",
}

// represents public.display_if_not_discussed_options -> for 'If not discussed' field
export enum DisplayIfNotDiscussedOptions {
    default = "default",
    hide = "hide",
    specified = "specified",
}

// display options for DisplayIfNotDiscussedOptions
export enum DisplayIfNotDiscussedOptionsToDisplay {
    "default" = "Default behavior",
    "hide" = "Hide field from output",
    "specified" = "Use a specified value",
}

export enum BroadcastTypes {
    success = "success",
    failed = "failed",
}

export enum BroadcastEvents {
    transcription = "transcription",
    document = "document",
    patientDeleted = "patient-deleted",
    patientEvent = "patient-event",
    documentDeleted = "document-deleted",
    documentCreated = "document-created",
    documentUpdated = "document-updated",
    documentSelection = "document-selection",
    emailConfirmationSent = "email-confirmation-sent",
    emailConfirmed = "email-confirmed",
    heartbeat = "heartbeat",
    phoneNumberVerificationStatusUpdated = "phone-number-verification-status-updated",
    callStatusUpdated = "call-status-updated",
}

export const BROADCAST_EVENT = "client-update";

export enum PatientRecordFetchErrors {
    NO_PATIENT_RECORD = "No Patient record found",
    FETCH_FAILED = "Failed to fetch patient record",
}

//enum for good bad and needs editing
export enum DocumentFeedbackEnum {
    Good = "Output Perfect",
    NeedsEditing = "Formatting Issues",
    Bad = "Information Issues",
}

export enum BuilderMagicStrings {
    PATIENT_ID = "patient_id",
    PATIENT_SPECIES = "patient_species",
    PATIENT_NAME = "patient_name",
    PATIENT_SEX = "patient_sex",
    PATIENT_BREED = "patient_breed",
    PATIENT_AGE = "patient_age",
    PATIENT_DOB = "patient_dob",

    DOCTOR_NAME = "doctor_name",
    CURRENT_DATE = "current_date",
    CURRENT_TIME = "current_time",
    CLINIC_NAME = "clinic_name",

    CONTACT_NAME = "contact_name",
    CONTACT_ID = "contact_id",
    CONTACT_PHONE = "contact_phone",
    CONTACT_EMAIL = "contact_email",
}

export const BuilderMagicStringExamples: Record<BuilderMagicStrings, string> = {
    [BuilderMagicStrings.PATIENT_ID]: "12345",
    [BuilderMagicStrings.PATIENT_SPECIES]: "Canine",
    [BuilderMagicStrings.PATIENT_NAME]: "Fluffy Smith",
    [BuilderMagicStrings.PATIENT_SEX]: "Female",
    [BuilderMagicStrings.PATIENT_BREED]: "Golden Retriever",
    [BuilderMagicStrings.PATIENT_AGE]: "5",
    [BuilderMagicStrings.PATIENT_DOB]: "2019-01-01",
    [BuilderMagicStrings.DOCTOR_NAME]: "Dr. Jane Doe",
    [BuilderMagicStrings.CURRENT_DATE]: "2024-07-18",
    [BuilderMagicStrings.CURRENT_TIME]: "2:30 PM",
    [BuilderMagicStrings.CLINIC_NAME]: "HappyDoc",
    [BuilderMagicStrings.CONTACT_NAME]: "Sam Smith",
    [BuilderMagicStrings.CONTACT_ID]: "C-001",
    [BuilderMagicStrings.CONTACT_PHONE]: "(555) 123-4567",
    [BuilderMagicStrings.CONTACT_EMAIL]: "sam@example.com",
};

export enum ModelLevel {
    Low = "low",
    Medium = "medium",
    High = "high",
}

export enum ReleaseNotesIssueType {
    New = "new",
    Improved = "improved",
    Fixed = "fixed",
}

//ERRORS

export const STREAMING_COMPLETION_ERROR =
    "Network issue detected. Please try again or contact support if the problem continues.";

export const FETCH_ERROR_REFRESH =
    "Error retrieving data. Please refresh the page. If the issue persists, contact support.";

export const NO_PATIENT_RECORD =
    "Error retrieving data. The patient may have been deleted. Please refresh or contact support if you believe this is an error.";

/**
 * Represents the status of an active patient.
 */
export enum ActivePatientStatus {
    /**
     * Indicates that the active patient is idle.
     */
    Idle = "idle",

    /**
     * Indicates that the active patient is currently loading.
     */
    Loading = "loading",

    /**
     * Indicates that the active patient has succeeded.
     */
    Succeeded = "succeeded",

    /**
     * Indicates that the active patient has failed.
     */
    Failed = "failed",
}

/**
 * Represents the data for an active patient.
 */
export type ActivePatientData = {
    patientVisits: VisitHeader[];
    patientData: PatientRecord | null;
    transcriptSections: TranscriptSection[];
    documentAssets: DocumentAsset[];
    multiplePatients: MultiPatientData[];
    status: ActivePatientStatus;
    error: PatientRecordFetchErrors | null;
    selectedAssetIds: string[];
    userHasDeselectedAsset: boolean;
    // Tombstones for sections removed locally — checked by the cross-client new-section
    // path in RealtimeProvider to prevent a delayed non-terminal broadcast (in-flight at
    // discard time) from resurrecting the section. Cleared on patient switch.
    discardedSectionIds: string[];
};

export type VisitHeader = {
    id: string;
    scheduledAt: string | null;
    updatedAt: string | null;
    displayName: string;
    isHistory: boolean;
    appointmentDuration?: number | null;
};

/**
 * Represents multi-patient data.
 */
export type MultiPatientData = {
    name: string;
    isSelected: boolean;
    index: number;
};

/**
 * Represents the PIMS integration
 */
export enum PIMSType {
    AVIMARK = "avimark",
    CORNERSTONE = "cornerstone",
    IMPROMED = "impromed",
    PULSE = "pulse_evet_practice",
    EZYVET = "ezyvet",
    HIPPO = "hippo",
    SHEPHERD = "shepherd",
    VETSPIRE = "vetspire",
    PROVET = "provet_cloud",
    DAYSMART = "daysmart",
    INSTINCT = "instinct",
    NEO = "neo",
    NAVETOR = "navetor",
    INTRAVET = "intravet",
    VETFM = "vetfm",
    WOOFWARE = "woofware",
    PETWARE = "petware",
    DIGITAIL = "digitail",
    FILE = "file",
    OTHER = "other",
}

/*
 * Maps the PIMS type to a proper display type
 */

export const PIMSDisplay = {
    [PIMSType.AVIMARK]: "Avimark",
    [PIMSType.CORNERSTONE]: "Cornerstone",
    [PIMSType.IMPROMED]: "Impromed",
    [PIMSType.PULSE]: "Pulse (eVet Practice)",
    [PIMSType.EZYVET]: "ezyVet",
    [PIMSType.HIPPO]: "Hippo",
    [PIMSType.SHEPHERD]: "Shepherd",
    [PIMSType.VETSPIRE]: "Vetspire",
    [PIMSType.PROVET]: "Provet Cloud",
    [PIMSType.DAYSMART]: "DaySmart",
    [PIMSType.INSTINCT]: "Instinct",
    [PIMSType.NEO]: "Neo",
    [PIMSType.NAVETOR]: "NaVetor",
    [PIMSType.INTRAVET]: "IntraVet",
    [PIMSType.VETFM]: "VetFM",
    [PIMSType.WOOFWARE]: "Woofware",
    [PIMSType.PETWARE]: "Petware",
    [PIMSType.DIGITAIL]: "Digitail",
    [PIMSType.FILE]: "Imported File",
    [PIMSType.OTHER]: "Other",
};

/**
 * Interface representing the payload for importing a file.
 */
export interface ImportFilePayload {
    /**
     * The timezone offset as hours from UTC. this is optional, but should be sent by the first request at least to
     * ensure that it is populated on the account record.
     */
    timezoneOffset?: number;

    /**
     * The URL of the file to be imported. assumed to be in CSV format and stored in supabase document storage
     */
    url: string;

    /**
     * Indicates if the file has a header row.
     * @default true
     */
    hasHeaderRow?: boolean;

    /**
     * The column index for the visit ID.
     */
    visitIdColumn: number;

    /**
     * The column index for the visit doctor.
     */
    visitDoctorColumn?: number;

    /**
     * The column index for the visit scheduled date and time.
     */
    visitScheduledAtColumn: number;

    /**
     * The column index for the visit appointment duration.
     */
    visitAppointmentDurationColumn: number;

    /**
     * The column index for the visit type.
     */
    visitTypeColumn?: number;

    /**
     * The column index for the patient's ID.
     */
    patientsIdColumn: number;

    /**
     * The column index for the patient's name.
     */
    patientsNameColumn: number;

    /**
     * The column index for the patient's sex.
     */
    patientsSexColumn?: number;

    /**
     * The column index for the patient's breed.
     */
    patientsBreedColumn?: number;

    /**
     * The column index for the patient's date of birth.
     */
    patientsDobColumn?: number;

    /**
     * The column index for the patient's species.
     */
    patientsSpeciesColumn?: number;

    /**
     * The column index for the contact's ID.
     */
    contactsIdColumn?: number;

    /**
     * The column index for the contact's first name.
     */
    contactsFirstNameColumn?: number;

    /**
     * The column index for the contact's last name.
     */
    contactsLastNameColumn?: number;

    /**
     * The column index for the contact's primary phone number.
     */
    contactsPrimaryPhoneColumn?: number;

    /**
     * The column index for the contact's primary email address.
     */
    contactsPrimaryEmailColumn?: number;
}

/**
 * Represents the response after importing a file.
 */
export type ImportFileResponse = {
    /**
     * Indicates whether the document is valid enough to be processed.
     */
    validDocument: boolean;

    /**
     * Describes the failure reason if the document is invalid.
     */
    documentFailure: string;

    /**
     * Contains the list of row failures with their respective offsets and failure reasons.
     */
    rowFailures: Array<{
        offset: number;
        failure: string;
    }>;

    /**
     * The number of records successfully created.
     */
    recordsCreated: number;
};

// Represents the mapping data for the File integration
export type DataMapping = {
    id: string;
    account_id: string;
    mapping_data: number[];
    is_active: boolean;
    created_at: string;
    updated_at: string;
};

/** patient_record_deleted_by_type */
export enum PatientRecordArchivedBy {
    User = "user",
    AppointmentSync = "appointment_sync",
}

/**
 * Outcome of a single EN-1088 recording-lease-guarded patient_record delete.
 *
 * PostgREST reports a refused update as `200` with an empty array, not an error - see "The
 * mechanism, verified" in the EN-1088 plan - so this distinguishes a live lease blocking the
 * delete ("refused") from a genuine success ("deleted") or a genuine Supabase/PostgREST
 * failure ("errored"). A bare boolean can't make that distinction, which is how a refused
 * delete was previously reported to the caller as a success.
 */
export type DeletePatientRecordOutcome = "deleted" | "refused" | "notFound" | "errored";

/**
 * "notFound" exists because a filtered UPDATE that matches nothing is indistinguishable, from
 * the response alone, from one the lease filter excluded - both are HTTP 200 with an empty
 * array. Collapsing them into "refused" told the vet a visit was "being recorded on another
 * device" when in fact it had simply been deleted elsewhere and their list hadn't refreshed:
 * invented recording state, the same class of lie EN-1088 exists to remove. So a zero-row
 * result is disambiguated with a follow-up existence check (deletePatientRecord in
 * supabaseClient.ts). "notFound" means the row is not there for this user at all - the end
 * state the vet asked for is already true, so it is neither an error nor a refusal, and must
 * NOT be counted in deletedIds (we did not delete it, and callers drive side effects off that).
 */
export type DeletePatientRecordsResult = {
    deletedIds: string[];
    refusedIds: string[];
    notFoundIds: string[];
    erroredIds: string[];
    /** EN-1494: every id is errored because the clinic resolved to another one than the list shows. */
    clinicChanged?: true;
};

// Chat message interface for document chat modal
export type ScoutIntent =
    | "edit-document"
    | "drop-file"
    | "create-document-template-from-pdf"
    | "document-question"
    | "question"
    | "unhandled";
export type ChatMessageActionStatus =
    | "accepted"
    | "rejected"
    | "cancelled"
    | "failed"
    | "retrying"
    | "pending"
    | "working"
    | "completed"
    | null;
export interface ChatMessage {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
    actionIntent?: ScoutIntent;
    actionStatus: ChatMessageActionStatus;
    retriedAt?: Date;
    retryFailedAt?: Date;
}
export type ChatMessagePossiblyOptimistic = Omit<ChatMessage, "timestamp"> & { timestamp: Date | null };

/**
 * Represents the automation script/definition stored in automation_json field.
 * This is the actual automation configuration that gets executed by the Chrome extension.
 */
export interface AutomationScript {
    id: string;
    name: string;
    version: string;
    description: string;
    debug?: boolean;
    defaultRetries?: number;
    defaultTimeoutMs?: number;
    parameterSchema?: ParameterSchema;
    actions: AutomationAction[];
    /**
     * Account that owns this automation (null for global/default automations). When the webapp
     * passes an automation inline to the extension, this MUST be forwarded so the extension treats
     * account-scoped automations as authoritative over any bundled default of the same id
     * (see Chrome-Ext-V2 resolveTriggeredAutomation).
     */
    account_id?: string | null;
}

/**
 * Represents an automation action. Actions can have various types and structures,
 * so we use a flexible type that allows for the dynamic nature of automation actions.
 */
export type AutomationAction = Record<string, any>;

/**
 * Represents an automation record from the database.
 * This is the database entity that contains metadata and the automation script.
 */
export interface Automation {
    id: string;
    automation_id: string;
    automation_json: AutomationScript;
    name: string;
    description: string;
    version: string;
    parameterSchema?: ParameterSchema;
    /** Account that owns this automation; null for global/default automations. */
    account_id?: string | null;
}

export interface ParameterDefinition {
    type: "string" | "password" | "url" | "number" | "boolean" | "object";
    required?: boolean;
    label: string;
    validation?: {
        pattern?: string; // Regex pattern for validation
        min?: number;
        max?: number;
    };
    default?: string; // Can reference other parameters: "{{instanceUrl}}/login.php"
    uiHint?: "text" | "password" | "textarea" | "select";
    description?: string;
}

export interface ParameterSchema {
    [key: string]: ParameterDefinition;
}

// Each sentiment-analysis category aggregated into this shape.
export interface CategoryAggregate {
    category: string; // e.g. "empathy"
    summary: string;
    oneCount: number; // number of visits with a rating of 1
    twoCount: number; // number of visits with a rating of 2
    threeCount: number; // number of visits with a rating of 3
    fourCount: number; // number of visits with a rating of 4
    fiveCount: number; // number of visits with a rating of 5
    average: number; // average rating across all visits
}

// Which entity a filter applies to.
export type FilterType = "doctor" | "account";

// Plain data shape for a visit's sentiment analysis ratings.
export interface VisitSentimentAnalysis {
    accountId: string;
    doctorId: string;
    id: string;
    ratings: {
        empathy?: number | null;
        patience?: number | null;
        clarity?: number | null;
        efficiency?: number | null;
        professional?: number | null;
        confidence?: number | null;
    };
}
